import React, { useState, useEffect } from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper} from '@mui/material'; 
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const DQResult = ({ results }) =>{

    const [totalRecords, setTotalRecords] = useState(0);
    const [completedRecords, setCompletedRecords] = useState(0);
    const [qualityScore, setQualityScore] = useState(0);
    const [notRunRecords, setNotRunRecords] = useState(0);
    const [passedRecords, setPassedRecords] = useState(0);
    const [weightage, setWeightage] = useState(null);
    const [chartData, setChartData] = useState(null);


useEffect(()=>{
    if (results) {
        const total = results.rules?.length || 0;
        
        const failed = results.rules?.filter(rule => 
            rule.dq_result === "fail" || rule.dq_result === "failure"
        )?.length || 0;
        
        const notRun = results.rules?.filter(rule => 
            rule.dq_result === "not_run" || 
            rule.dq_result === "NOT_RUN" || 
            rule.dq_result === "Not Run"
        )?.length || 0;
        
        const passed = results.rules?.filter(rule => 
            rule.dq_result === "pass"
        )?.length || 0;
        
        const score = results.latest_dimension_scores?.score || 0;
        const dimensionWeightage = results.latest_dimension_scores || {};
        const chart = results?.score_trend_plot_config;
        const updatedWeightage = { ...dimensionWeightage }; 
        delete updatedWeightage.score; 
        
        setTotalRecords(total);
        setCompletedRecords(failed);  
        setNotRunRecords(notRun);     
        setPassedRecords(passed);     
        setQualityScore(score);
        setWeightage(updatedWeightage);
        setChartData(chart);
    }
}, [results])

    // Transform Plotly data to Recharts format
    const transformChartData = (plotlyData) => {
        if (!plotlyData || !plotlyData.data || !plotlyData.data[0]) return [];
        
        const xData = plotlyData.data[0].x || [];
        const yData = plotlyData.data[0].y || [];
        
        return xData.map((date, index) => {
            // Parse date and format it
            const dateObj = new Date(date);
            const displayDate = dateObj.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
            
            return {
                date: date,
                displayDate: displayDate,
                score: yData[index]
            };
        });
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '8px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '12px' }}>
                        Date: {payload[0].payload.date}
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#bb1ccc' }}>
                        Score: {payload[0].value}
                    </p>
                </div>
            );
        }
        return null;
    };

    const getQualityScoreColor = (score) => {
        if (score > 85) return '#4CAF50';
        if (score >= 55) return '#FF9800';
        if (score < 55) return '#F44336';
        return '#000';
    };

    const SimpleStatCard = ({ title, value, color = '#1E88E5', isDynamic = false }) => (
  <Box
    sx={{
      backgroundColor: '#fff',
      borderRadius: '16px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      padding: 3,
      width: '200px',
      height: '120px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      border: '1px solid #d3d3d3',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2) !important',
        transform: 'translateY(-6px) !important',
        border: '1px solid #bb1ccc !important',
        backgroundColor: '#f8f9ff'
      }
    }}
  >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color, fontSize: '1.1rem' }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              height: '3px',
              width: '40px',
              backgroundColor: color,
              borderRadius: '2px',
              mt: 0.5,
            }}
          />
        </Box>
    
<Typography
  variant="h4"
  sx={{color: isDynamic ? getQualityScoreColor(value) : color, fontWeight: 700,  fontSize: '2rem'}}
>
  {value}
</Typography>
      </Box>
    );

    return (
        <div>
          {/* Disclaimer Section */}
        

          <div className="flex w-full">
            <Box sx={{ display: 'flex', flexWrap:'wrap', gap: 4, mb: 2 }}>
              <SimpleStatCard 
                title="DQ Score" 
                value={qualityScore || 0} 
                color="#bb1ccc" 
                isDynamic={true}
              />
              <SimpleStatCard 
                title="Rules Processed" 
                value={totalRecords || 0} 
                color="#64748b"  
                valueColor="#1e293b"
              />
              <SimpleStatCard 
                title="Rules Passed" 
                value={passedRecords || 0} 
                color="#10b981"
                valueColor="#059669"
              /> 
              <SimpleStatCard 
                title="Rules Failed" 
                value={completedRecords || 0} 
                color="#ef4444"
                valueColor="#dc2626"
              /> 
              <SimpleStatCard 
                title="Rules Not Executed" 
                value={notRunRecords || 0} 
                color="#f59e0b"
                valueColor="#d97706"
              />  
            </Box>
          </div>

          <div className="flex w-full items-stretch">
            <div className="w-1/2">
          <Box 
            sx={{ 
              backgroundColor: '#fff',
              borderRadius: '16px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              padding: 3,
              border: '1px solid #d3d3d3',
              marginBottom: 2,
              height: '100%'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{
                color: '#bb1ccc',
                fontWeight: 700,
                fontSize: '1.1rem',
                marginBottom: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: '4px',
                  height: '20px',
                  backgroundColor: '#bb1ccc',
                  borderRadius: '2px'
                }}
              />
              Dimension Level Score
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: 3,
              maxWidth: '100%'
            }}>
              {weightage && Object.entries(weightage)
                .filter(([key]) => key.endsWith('_weightage')) // Only process weightage entries
                .map(([key, value]) => {
                  // Extract score and weightage from the API response structure
                  const dimensionName = key.replace('_weightage', '');
                  const score = results?.latest_dimension_scores?.[dimensionName] || 0;
                  const weightageValue = value;
                  const contribution = score > 0 && weightageValue > 0 ? (score * weightageValue / 100).toFixed(2) : 0;
                  
                  return (
                    <Box 
                      key={key} 
                      sx={{
                        backgroundColor: weightageValue > 0 ? '#f0f9ff' : '#e5e2e2',
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        padding: 3,
                        border: '1px solid',
                        borderColor: weightageValue > 0 ? '#e0f2fe' : '#e5e2e2',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: '#1f2937',
                          marginBottom: 2,
                          textTransform: 'uppercase',
                          textAlign: 'center'
                        }}
                      >
                        {dimensionName.charAt(0).toUpperCase() + dimensionName.slice(1)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Score */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: '#6b7280'
                            }}
                          >
                            Score
                          </Typography>
                          <Typography 
                            sx={{
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              color: score > 0 ? '#059669' : '#dc2626'
                            }}
                          >
                            {score}
                          </Typography>
                        </Box>
                        
                        {/* Weightage */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: '#6b7280'
                            }}
                          >
                            Weightage
                          </Typography>
                          <Typography 
                            sx={{
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              color: '#374151'
                            }}
                          >
                            {weightageValue}%
                          </Typography>
                        </Box>
                        
                        {/* Contribution */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              color: '#6b7280'
                            }}
                          >
                            Contribution
                          </Typography>
                          <Typography 
                            sx={{
                              fontWeight: 700,
                              fontSize: '1.1rem',
                              color: score > 0 ? '#059669' : '#dc2626'
                            }}
                          >
                            {contribution}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          </Box>
          </div>
          <div className="w-1/2 pl-4">
            <Box 
              className="shadow-xl bg-white p-4 rounded" 
              sx={{
                border: "2px solid #f5f5f5", 
                padding: "16px", 
                width: "100%", 
                height: "100%",
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }} 
            >
              <Typography variant="body2" color="text.secondary" sx={{fontWeight: "bold", mb: 1}}>
                DQ Score Trend
              </Typography>
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'stretch' }}>
                {chartData && chartData.data && chartData.data[0] ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart 
                      data={transformChartData(chartData)}
                      margin={{ top: 20, right: 40, left: 10, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="displayDate" 
                        angle={-30}
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 12 }}
                        label={{ 
                          value: chartData.layout?.xaxis?.title || 'Execution Date', 
                          position: 'insideBottom', 
                          offset: -50, 
                          style: { fontSize: 14 } 
                        }}
                      />
                      <YAxis 
                        domain={[0, 100]}
                        tick={{ fontSize: 12 }}
                        label={{ 
                          value: chartData.layout?.yaxis?.title || 'Quality Score', 
                          angle: -90, 
                          position: 'insideLeft', 
                          style: { fontSize: 14 } 
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#bb1ccc" 
                        strokeWidth={2}
                        dot={{ fill: "#bb1ccc", r: 4 }}
                        name="Quality Score"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    color: '#999'
                  }}>
                    No chart data available
                  </Box>
                )}
              </Box>
            </Box>
          </div>
          </div>

          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
              border: '1px solid #e0e0e0', 
              borderRadius: '12px',
              marginTop: "30px",
             marginBottom:'5rem'
            }}
          >
            <Table sx={{ minWidth: 650 }} stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#bb1ccc !important'
                  }}>
                    Column Name
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#bb1ccc !important'
                  }}>
                    CDE
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#bb1ccc !important'
                  }}>
                    Rule Description
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#bb1ccc !important'
                  }}>
                    Dimension
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#bb1ccc !important'
                  }}>
                    Keywords
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#bb1ccc !important'
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ 
                    color: "white", 
                    fontWeight: 600, 
                    fontSize: '14px',
                    padding: '16px 20px',
                    borderBottom: '2px solid #000080',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#bb1ccc !important'
                  }}>
                    Score
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {results?.rules?.length > 0 ? (
                  results.rules.map((rule, index) => (
                    <TableRow 
                      key={rule.rule_name}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                        '&:hover': {
                          backgroundColor: '#f0f8ff',
                          transition: 'background-color 0.2s ease'
                        }
                      }}
                    >
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#2c3e50'
                      }}>
                        {rule.check_type === "Asset Level Check" ? "Asset Level Rule" : rule.column_name}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#2c3e50'
                      }}>
                        {rule.is_cde === "N/A" || rule?.is_cde === false ? "No"  : "Yes"}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#555',
                        maxWidth: '300px',
                        wordWrap: 'break-word'
                      }}>
                        {rule.rule_description}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#555',
                        textTransform: 'capitalize'
                      }}>
                        {rule.dimension_name.charAt(0).toUpperCase() + rule.dimension_name.slice(1)}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#555',
                        textTransform: 'capitalize'
                      }}>
                        {rule.keywords}
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px'
                      }}>
                        <Box
                          sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 2,
                              py: 0.5,
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              backgroundColor: 
                                  rule.dq_result === "pass" ? '#e8f5e8' : 
                                  (rule.dq_result === "fail" || rule.dq_result === "FAILURE") ? '#ffeaea' : 
                                  (rule.dq_result === "not_run" || rule.dq_result === "NOT_RUN" || rule.dq_result === "Not Run") ? '#fff3e0' : 
                                  '#ffeaea',
                              color: 
                                  rule.dq_result === "pass" ? '#2e7d32' : 
                                  (rule.dq_result === "fail" || rule.dq_result === "FAILURE") ? '#d32f2f' : 
                                  (rule.dq_result === "not_run" || rule.dq_result === "NOT_RUN" || rule.dq_result === "Not Run") ? '#f57c00' : 
                                  '#d32f2f'
                            }}
                          >
                            {rule.dq_result === "pass" ? "Pass" : 
                            (rule.dq_result === "fail" || rule.dq_result === "FAILURE") ? "Fail" : 
                            (rule.dq_result === "not_run" || rule.dq_result === "NOT_RUN" || rule.dq_result === "Not Run") ? "Not Run" : 
                            "N/A"}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: getQualityScoreColor(rule.quality_score)
                      }}>
                        {rule.quality_score}%
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell 
                      colSpan={5} 
                      align="center"
                      sx={{ 
                        padding: '40px 20px',
                        fontSize: '16px',
                        color: '#666',
                        fontStyle: 'italic'
                      }}
                    >
                      No rules found for the selected dataset.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
    )
}